/**
 * MCP Tools catalog + secret-store API.
 *
 *   GET  /tools                  — list registered tools (with UI hints)
 *   POST /tools/secrets          — write SecureString for an ``x-secret`` field
 *   GET  /tools/secrets-registry — list reusable credential registry rows
 *   POST /tools/secrets-registry — create registry row (+ SSM writes server-side)
 *   POST /tools/secrets-registry/_adopt — promote inline Run-tab secrets into reusable registry (+ optional agent rewrite)
 *   DELETE /tools/secrets-registry/{id} — remove row when no agent references secretId (SSM paths not erased)
 *   POST /tools/probe            — invoke ``generic-rest-api`` Lambda (Creator) for Test connection
 *
 * These routes are Cognito-JWT-gated. ``POST /tools/secrets`` and registry writes additionally
 * requires Creator-tier RBAC server-side. The frontend never receives the
 * secret value back — only the SSM parameter name (the path the agent's
 * ``secretRefs`` map points at).
 *
 * Path conventions (mirror ``stepflow_core.secret_paths``):
 *
 *   /stepflow/secrets/{env}/tenant/{tenantId}/networks/{networkId}/pools/{poolSlug}/agents/{agentId}/tools/{instanceId}/{configKey}
 *
 * ``tenantId`` is the workspace tenant (``TN-…``) and is the outer
 * scope so the put broker can reject any path whose tenant segment
 * doesn't match the JWT claim. ``poolSlug`` is the literal string
 * ``workbench`` while a Creator is editing in the Run tab; the
 * release-time cloner rewrites that one segment to the live pool slug
 * when the network ships, leaving every other segment intact.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const WORKBENCH_POOL_SLUG = 'workbench';

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Stable per-tool-mount id ("ti_xxxxxxxx"). Same shape as the backend's
 * ``stepflow_core.secret_paths.new_tool_instance_id`` — duplicated here
 * so the dialog can mint one before the row exists. The validator only
 * checks the *shape* of the ids in ``secretRefs`` paths; collisions are
 * astronomically unlikely.
 */
export function newToolInstanceId() {
  const bytes = new Uint8Array(4);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  return `ti_${[...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Feature flag ``stepflow.secretsRegistry.v1`` (mirror server ``STEPFLOW_SECRETS_REGISTRY_V1``).
 * Set ``VITE_STEPFLOW_SECRETS_REGISTRY_V1=false`` to hide registry UI surfaces.
 */
export function secretsRegistryV1Enabled() {
  const v = (import.meta.env?.VITE_STEPFLOW_SECRETS_REGISTRY_V1 ?? '1').toString().trim().toLowerCase();
  return !['0', 'false', 'no', 'off'].includes(v);
}

/**
 * Build the canonical SSM path for a tool secret. Used by the dialog
 * before calling ``putToolSecret``; round-trips through the validator
 * server-side so a typo here surfaces as a 400, not silent corruption.
 */
export function buildToolSecretPath({
  env,
  tenantId,
  networkId,
  agentId,
  instanceId,
  configKey,
  poolSlug = WORKBENCH_POOL_SLUG,
}) {
  if (!env) throw new Error('env is required');
  if (!tenantId) throw new Error('tenantId is required');
  if (!networkId) throw new Error('networkId is required');
  if (!agentId) throw new Error('agentId is required');
  if (!instanceId) throw new Error('instanceId is required');
  if (!configKey) throw new Error('configKey is required');
  return (
    `/stepflow/secrets/${env}` +
    `/tenant/${tenantId}` +
    `/networks/${networkId}` +
    `/pools/${poolSlug}` +
    `/agents/${agentId}` +
    `/tools/${instanceId}` +
    `/${configKey}`
  );
}

/**
 * GET /tools — flat list of registered MCP tools with UI hints.
 *
 * @param {{ forAgent?: boolean }} [options]
 *        When ``forAgent: true``, only rows with catalog ``toolType: service``.
 *
 * @returns {Promise<{ tools: Array<{
 *   name: string,
 *   description: string,
 *   inputSchema: Record<string, any>,
 *   secretFields: string[],
 *   uiTypes: Record<string, string>,
 *   promptInjections: Record<string, string>,
 *   enabled: boolean,
 *   policy?: Record<string, any>,
 *   toolType?: 'service' | 'memory' | 'system',
 * }> }>}
 */
export async function listTools(options = {}) {
  const { forAgent = false } = options;
  const qs = forAgent ? '?forAgent=1' : '';
  const data = await authedFetch(`/tools${qs}`);
  const tools = Array.isArray(data?.tools) ? data.tools : [];
  return { tools };
}

/**
 * GET /tools/mcp-registry — the platform MCP allowlist for the agent's
 * "enable MCP" toggles. Read-only catalog; never returns spawn config or
 * credentials.
 *
 * @returns {Promise<{ mcps: Array<{
 *   id: string, displayName: string, description: string,
 *   authRequired: boolean, sandbox: boolean,
 *   credentialFields: Array<{ key: string, label: string, hint?: string,
 *     required: boolean, secret: boolean }>,
 * }> }>}
 */
export async function listMcpRegistry() {
  const data = await authedFetch('/tools/mcp-registry');
  const mcps = Array.isArray(data?.mcps) ? data.mcps : [];
  return { mcps };
}

// ---------------------------------------------------------------------------
// Scoped MCP credentials (TENANT / NETWORK / POOL) — power the shared
// "Connections" (SecretsCatalog) catalog. Scope travels in the body; the list
// uses networkId/poolId query params for inheritance-chain visibility. Mirrors
// the secrets-registry helpers above. (Per-AGENT MCP creds use poolMembersApi.)
// ---------------------------------------------------------------------------

/**
 * GET /tools/mcp-credentials/registry — configured MCP credential rows visible
 * at the mount scope. Never returns secret material.
 * @param {{ networkId?: string, poolId?: string }} [options]
 * @returns {Promise<{ mcpCredentials: Array<{ mcpId, displayName, scope, scopeId, setBy, setAt, configured }> }>}
 */
export async function listMcpCredentials(options = {}) {
  const { networkId = '', poolId = '' } = options;
  const sp = new URLSearchParams();
  if (networkId) sp.set('networkId', networkId);
  if (poolId) sp.set('poolId', poolId);
  const qs = sp.toString();
  const path = qs ? `/tools/mcp-credentials/registry?${qs}` : '/tools/mcp-credentials/registry';
  const data = await authedFetch(path);
  const mcpCredentials = Array.isArray(data?.mcpCredentials) ? data.mcpCredentials : [];
  return { mcpCredentials };
}

/**
 * POST /tools/mcp-credentials/registry — create/replace an MCP credential at a
 * scope. Plaintext goes to SSM via the broker; only the pointer is stored.
 * @param {{ mcpId: string, scope: { kind: 'TENANT'|'NETWORK'|'POOL', networkId?: string, poolId?: string }, credentials: Record<string, unknown> }} body
 */
export async function createMcpCredential(body) {
  if (!body || typeof body !== 'object') throw new Error('body is required');
  if (!String(body.mcpId || '').trim()) throw new Error('mcpId is required');
  return authedFetch('/tools/mcp-credentials/registry', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** POST /tools/mcp-credentials/registry/delete — clear one scoped MCP credential. */
export async function deleteMcpCredential(body) {
  if (!body || typeof body !== 'object') throw new Error('body is required');
  if (!String(body.mcpId || '').trim()) throw new Error('mcpId is required');
  return authedFetch('/tools/mcp-credentials/registry/delete', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /tools/mcp-credentials/registry/test — verify a credential WITHOUT
 * saving. Verifies the CREDENTIAL (e.g. PAT valid), NOT that the MCP server
 * boots. Only for MCPs with a registered probe.
 * @returns {Promise<{ ok: boolean, message: string, details?: object }>}
 */
export async function testMcpCredentialScoped(body) {
  if (!body || typeof body !== 'object') throw new Error('body is required');
  if (!String(body.mcpId || '').trim()) throw new Error('mcpId is required');
  return authedFetch('/tools/mcp-credentials/registry/test', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /tools/mcp-credentials/registry/reveal — rehydrate one saved MCP
 * credential for the Edit wizard. Returns the saved NON-SECRET field values
 * (prefilled into the wizard) + ``secretsSet`` (secret fields already set, shown
 * masked). The secret value itself is never returned. Parity with connector edit.
 * @returns {Promise<{ mcpId, instanceId, label, scope, values, secretsSet, allowedKeys }>}
 */
export async function revealMcpCredentialScoped(body) {
  if (!body || typeof body !== 'object') throw new Error('body is required');
  if (!String(body.mcpId || '').trim()) throw new Error('mcpId is required');
  return authedFetch('/tools/mcp-credentials/registry/reveal', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /tools/secrets — write a SecureString for one ``x-secret`` field.
 * The secret value never round-trips back; the response confirms the
 * SSM path the agent's ``secretRefs`` should reference.
 *
 * @param {{ parameterName: string, value: string }} body
 */
export async function putToolSecret({ parameterName, value }) {
  if (!parameterName) throw new Error('parameterName is required');
  if (value === undefined || value === null || value === '') {
    throw new Error('value is required');
  }
  return authedFetch('/tools/secrets', {
    method: 'POST',
    body: JSON.stringify({ parameterName, value }),
  });
}

/**
 * POST /tools/probe — Creator-tier invoke of the ``generic-rest-api`` MCP
 * Lambda with an in-memory credential (not persisted). Powers “Test connection”
 * in the Workbench wizard.
 *
 * @param {{
 *   networkId: string,
 *   config: { BASE_URL: string, AUTH_TYPE: string, API_SPECIFICATION: string, CUSTOM_HEADER_NAME?: string },
 *   credential: string,
 *   probeRequest: { method?: string, path?: string, query?: unknown, body?: unknown, headers?: unknown },
 * }} params
 * @returns {Promise<{ ok?: boolean, lambdaError?: string, result?: Record<string, unknown> }>}
 */
export async function probeGenericRestApi({
  networkId,
  config,
  credential = '',
  probeRequest = {},
}) {
  if (!networkId) throw new Error('networkId is required');
  if (!config || typeof config !== 'object') throw new Error('config is required');
  return authedFetch('/tools/probe', {
    method: 'POST',
    body: JSON.stringify({
      toolName: 'generic-rest-api',
      networkId,
      config,
      credential: credential === undefined || credential === null ? '' : String(credential),
      probeRequest,
    }),
  });
}

/**
 * PUT /tools/{name}/policy — admin-only — flip a tool's
 * workspace-wide enabled flag.
 *
 * @param {string} name
 * @param {{ enabled: boolean, reason?: string }} body
 * @returns {Promise<{ ok: boolean, name: string, policy: {
 *   enabled: boolean, reason: string, actorSub: string,
 *   updatedAt: string, source: 'policy' | 'default',
 * } }>}
 */
export async function setToolPolicy(name, { enabled, reason = '' } = {}) {
  if (!name) throw new Error('tool name is required');
  if (typeof enabled !== 'boolean') throw new Error('enabled must be boolean');
  return authedFetch(`/tools/${encodeURIComponent(name)}/policy`, {
    method: 'PUT',
    body: JSON.stringify({ enabled, reason: String(reason || '') }),
  });
}

/**
 * GET /tools/{name}/usage — admin-only — list every agent in the
 * tenant currently configured to use ``name``. Powers the
 * "Tags / Usage" affordance in the Workspace Tools panel so admins
 * see what they'll break before disabling.
 *
 * @param {string} name
 * @returns {Promise<{ name: string, count: number, usage: Array<{
 *   networkId: string, agentId: string, instanceId: string,
 * }> }>}
 */
export async function getToolUsage(name) {
  if (!name) throw new Error('tool name is required');
  const data = await authedFetch(`/tools/${encodeURIComponent(name)}/usage`);
  return {
    name: data?.name || name,
    count: typeof data?.count === 'number' ? data.count : (data?.usage || []).length,
    usage: Array.isArray(data?.usage) ? data.usage : [],
  };
}

/**
 * GET /tools/secrets-registry — list reusable tool secrets for the workspace tenant.
 *
 * Connector context hierarchy (see ``.cursor/rules/connector-context-hierarchy.mdc``):
 *
 *   - No filters → tenant-level rows only (POOL rows are intentionally hidden
 *     from the workbench registry index; they only surface to their pool's
 *     members via ``poolId``).
 *   - ``networkId`` → tenant + that network's NETWORK-scoped rows.
 *   - ``networkId + poolId`` → full inheritance chain the pool can attach:
 *     TENANT ∪ NETWORK ∪ this POOL. Sibling pools never appear.
 *
 * @param {{ toolName?: string, q?: string, networkId?: string, poolId?: string }} [options]
 * @returns {Promise<{ secrets: Array<Record<string, unknown>>, count: number }>}
 */
export async function listSecretsRegistry(options = {}) {
  const { toolName = '', q = '', networkId = '', poolId = '' } = options;
  const sp = new URLSearchParams();
  if (toolName) sp.set('toolName', toolName);
  if (q) sp.set('q', q);
  if (networkId) sp.set('networkId', networkId);
  if (poolId) sp.set('poolId', poolId);
  const qs = sp.toString();
  const path = qs ? `/tools/secrets-registry?${qs}` : '/tools/secrets-registry';
  const data = await authedFetch(path);
  const secrets = Array.isArray(data?.secrets) ? data.secrets : [];
  return {
    secrets,
    count: typeof data?.count === 'number' ? data.count : secrets.length,
  };
}

/**
 * GET /tools/secrets-registry/{secretId} — full registry row (config, secretRefs metadata).
 *
 * @param {string} secretId
 */
export async function getSecretsRegistrySecret(secretId) {
  const sid = String(secretId || '').trim();
  if (!sid) throw new Error('secretId is required');
  const data = await authedFetch(`/tools/secrets-registry/${encodeURIComponent(sid)}`);
  const secret = data?.secret && typeof data.secret === 'object' ? data.secret : null;
  if (!secret) throw new Error('Secret not found');
  return { secret };
}

/**
 * GET /tools/secrets-registry/{secretId}/references — agents that mount this registry secret.
 *
 * @param {string} secretId
 * @returns {Promise<{ secretId: string, references: Array<{
 *   networkId: string, agentId: string, instanceId: string, toolName: string
 * }>, count: number }>}
 */
export async function getSecretsRegistryReferences(secretId) {
  const sid = String(secretId || '').trim();
  if (!sid) throw new Error('secretId is required');
  const data = await authedFetch(
    `/tools/secrets-registry/${encodeURIComponent(sid)}/references`,
  );
  const references = Array.isArray(data?.references) ? data.references : [];
  return {
    secretId: String(data?.secretId || sid),
    references,
    count: typeof data?.count === 'number' ? data.count : references.length,
  };
}

/**
 * POST /tools/secrets-registry — create metadata + writes SSM for plaintext secrets server-side.
 *
 * @param {{
 *   toolName: string,
 *   secretName: string,
 *   authType?: string,
 *   config?: Record<string, unknown>,
 *   plaintextSecrets?: Record<string, string>,
 *   scope?: { kind?: string, networkId?: string },
 * }} body
 */
export async function createSecretsRegistrySecret(body) {
  if (!body || typeof body !== 'object') throw new Error('body is required');
  const toolName = String(body.toolName || '').trim();
  const secretName = String(body.secretName || '').trim();
  if (!toolName) throw new Error('toolName is required');
  if (!secretName) throw new Error('secretName is required');
  return authedFetch('/tools/secrets-registry', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * POST /tools/secrets-registry/_adopt — copy inline workbench ``secretRefs`` from an agent mount
 * into the registry (creates reusable SSM paths). Default ``replaceInline`` on the API rewires that
 * mount to ``secretId`` after copy.
 *
 * @param {{
 *   networkId: string,
 *   agentId: string,
 *   instanceId: string,
 *   toolName: string,
 *   secretName: string,
 *   replaceInline?: boolean,
 *   scope?: Record<string, unknown>,
 *   authType?: string,
 * }} params
 */
export async function adoptInlineSecretToRegistry(params) {
  if (!params || typeof params !== 'object') throw new Error('params is required');
  const networkId = String(params.networkId || '').trim();
  const agentId = String(params.agentId || '').trim();
  const instanceId = String(params.instanceId || '').trim();
  const toolName = String(params.toolName || '').trim();
  const secretName = String(params.secretName || '').trim();
  if (!networkId) throw new Error('networkId is required');
  if (!agentId) throw new Error('agentId is required');
  if (!instanceId) throw new Error('instanceId is required');
  if (!toolName) throw new Error('toolName is required');
  if (!secretName) throw new Error('secretName is required');
  const body = {
    networkId,
    agentId,
    instanceId,
    toolName,
    secretName,
  };
  if (params.replaceInline !== undefined) {
    body.replaceInline = Boolean(params.replaceInline);
  }
  if (params.scope !== undefined) {
    body.scope = params.scope;
  }
  if (params.authType !== undefined) {
    body.authType = params.authType;
  }
  return authedFetch('/tools/secrets-registry/_adopt', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * PUT /tools/secrets-registry/{secretId} — merge metadata/config and optionally rotate secrets.
 *
 * @param {string} secretId
 * @param {Record<string, unknown>} patch — e.g. `{ secretName, authType, scope, config, plaintextSecrets }`
 */
export async function updateSecretsRegistrySecret(secretId, patch, options = {}) {
  const sid = String(secretId || '').trim();
  if (!sid) throw new Error('secretId is required');
  if (!patch || typeof patch !== 'object') throw new Error('patch body is required');
  const ifm = options.ifMatch;
  const extraHeaders =
    ifm !== undefined && ifm !== null && String(ifm).trim() !== '' ? { 'If-Match': String(ifm).trim() } : {};
  return authedFetch(`/tools/secrets-registry/${encodeURIComponent(sid)}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
    headers: extraHeaders,
  });
}

/**
 * POST /tools/secrets-registry/{secretId}/test — resolve SSM-backed secrets via keys-get broker,
 * invoke generic-rest-api probe, persist lastTestedAt / testStatus / lastTestError on the row.
 *
 * @param {string} secretId
 * @param {{ networkId?: string, probeRequest?: Record<string, unknown> }} body
 *   ``networkId`` is ignored; probes use tenant auth (``__registry_probe__``) like ``POST /tools/probe``.
 */
export async function testSecretsRegistrySecret(secretId, body = {}) {
  const sid = String(secretId || '').trim();
  if (!sid) throw new Error('secretId is required');
  const payload = body && typeof body === 'object' ? body : {};
  return authedFetch(`/tools/secrets-registry/${encodeURIComponent(sid)}/test`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

/**
 * DELETE /tools/secrets-registry/{secretId} — remove registry metadata when unused.
 * SSM values under historic ``secretRefs`` are not removed automatically.
 */
export async function deleteSecretsRegistrySecret(secretId) {
  const sid = String(secretId || '').trim();
  if (!sid) throw new Error('secretId is required');
  return authedFetch(`/tools/secrets-registry/${encodeURIComponent(sid)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Read-only agent workspace browser ("the desk"). Serves the agent's persistent
// working-tree snapshot (last run) — file tree, one file's content, full
// download. Point-in-time, never live; the UI labels it "as of last run".
// ---------------------------------------------------------------------------
function _enc(s) { return encodeURIComponent(String(s || '').trim()); }

/** GET ONE folder's immediate children (lazy). ``prefix`` = subfolder path
 *  ('' = workspace root). → { savedAt, kind, prefix, breadcrumb:[{name,path}],
 *  entries:[{name,path,isDir,size,common,text,contentType}], cold? } */
export async function getAgentWorkspaceTree(networkId, agentId, prefix = '', { workspaceId = '' } = {}) {
  const p = new URLSearchParams();
  if (prefix) p.set('prefix', String(prefix));
  // ``workspaceId`` switches the browser from the per-agent desk to the SHARED
  // room workspace content tree (the whole /workspace/repo: manuscript + the
  // build/ deliverable). Backend ``_ws_root`` resolves it (doc/arch/ASSETS.md).
  if (workspaceId) p.set('workspaceId', String(workspaceId));
  const qs = p.toString() ? `?${p.toString()}` : '';
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/tree${qs}`);
}

/** GET one file's content from the snapshot. → { path, size, text, content } or { binary:true } */
export async function getAgentWorkspaceFile(networkId, agentId, path, { workspaceId = '' } = {}) {
  const p = new URLSearchParams();
  p.set('path', String(path || ''));
  if (workspaceId) p.set('workspaceId', String(workspaceId));
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/file?${p.toString()}`);
}

/** DELETE a user upload/output on demand (doc/arch/ATTACHMENTS.md). ``path`` may
 *  be a single file (uploads/<runId>/x.pdf) or a batch dir (uploads/<runId>/).
 *  Only paths under uploads/ or outputs/ are deletable. Optional ``workspaceId``
 *  targets a shared workspace instead of the per-agent desk. → { deleted, path } */
export async function deleteAgentWorkspaceFile(networkId, agentId, path, { workspaceId = '' } = {}) {
  const p = new URLSearchParams({ path: String(path || '') });
  if (workspaceId) p.set('workspaceId', String(workspaceId));
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/file?${p.toString()}`,
    { method: 'DELETE' });
}

/** GET a presigned URL to download the whole workspace as a tar. ``workspaceId``
 *  targets the shared room workspace instead of the per-agent desk.
 *  → { url, expiresIn, bytes, savedAt } */
export async function getAgentWorkspaceDownloadUrl(networkId, agentId, { workspaceId = '' } = {}) {
  const qs = workspaceId ? `?workspaceId=${encodeURIComponent(String(workspaceId))}` : '';
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/download${qs}`);
}

/** GET a presigned URL for ONE file's S3 object (any type) — renders pdf/docx/
 *  xlsx/images natively in the viewer, and powers per-file download. Pass
 *  ``download:true`` to get an attachment disposition. → { url, path, expiresIn } */
export async function getAgentWorkspaceRawUrl(networkId, agentId, path, { download = false, workspaceId = '' } = {}) {
  const p = new URLSearchParams();
  p.set('path', String(path || ''));
  if (download) p.set('download', '1');
  if (workspaceId) p.set('workspaceId', String(workspaceId));
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/raw?${p.toString()}`);
}

// ---------------------------------------------------------------------------
// Shared workspaces — scope-pinned mutable workspaces (doc/arch/ASSETS.md). An
// uploaded zip/tar is unpacked to a permanent S3 prefix, attached to builder
// agents, and MUTATED in place. Scope (TENANT/NETWORK/POOL) is AMBIENT — set by
// the catalog tab it's created in, exactly like connectors. (Distinct from the
// read-only Data Plane Asset / knowledge.)
// ---------------------------------------------------------------------------

/** POST create workspace. Two shapes:
 *  - upload:        { label, filename, scope } → { workspaceId, uploadUrl, … }
 *  - from template: { label, templateId, scope } → { workspaceId, testStatus:'TESTED_OK', … }
 *    (server-side S3-copies the template's content/; no upload step) */
export async function createWorkspace(body) {
  return authedFetch('/tools/workspaces', { method: 'POST', body: JSON.stringify(body || {}) });
}

/** PUT edit an existing workspace's SKILLS (full-sync). The source files
 *  (template / uploaded archive) are immutable; only the skills layer changes.
 *  Body: { skillIds?: string[], tenantSkillIds?: string[] } — the new DESIRED
 *  set (skills dropped from the list have their files removed; added ones are
 *  copied in). → { workspaceId, skillIds, tenantSkills, fileCount, sizeBytes }. */
export async function updateWorkspace(workspaceId, body) {
  return authedFetch(`/tools/workspaces/${_enc(workspaceId)}`, {
    method: 'PUT', body: JSON.stringify(body || {}),
  });
}

/** GET StepFlow-hosted workspace templates (doc/arch/TEMPLATES.md) for the
 *  bootstrap dropdown. → { templates: [{id, label, description}] } */
export async function listWorkspaceTemplates() {
  return authedFetch('/tools/workspace-templates');
}

/** GET curated open-source skills (doc/arch/TEMPLATES.md §Phase 2) for the
 *  wizard 'Add skills' multi-select. → { skills: [{id, label, description, license}] }
 *  Selected ids ride into createWorkspace({skillIds}) → copied into the new
 *  workspace's .claude/skills/ (allowlist-gated server-side). */
export async function listWorkspaceSkills() {
  return authedFetch('/tools/workspace-skills');
}

/** GET one skill's full detail for the discover→detail panel.
 *  → { id, label, description, license, body }  (body = full SKILL.md markdown;
 *  empty if the tree isn't synced yet — UI falls back to description). */
export async function getWorkspaceSkillDetail(skillId) {
  return authedFetch(`/tools/workspace-skills/${_enc(skillId)}`);
}

/** Upload the archive directly to S3 via the presigned PUT URL (no auth header —
 *  the signature carries it). The browser streams the File/Blob as-is. */
export async function uploadWorkspaceArchive(uploadUrl, file) {
  const res = await fetch(uploadUrl, { method: 'PUT', body: file });
  if (!res.ok) {
    const err = new Error(`Upload failed (HTTP ${res.status})`);
    err.status = res.status;
    throw err;
  }
  return true;
}

/** POST unpack the uploaded archive → content objects + pointer + zip.
 *  → { workspaceId, testStatus, fileCount, sizeBytes, workspacePrefix } */
export async function unpackWorkspace(workspaceId) {
  return authedFetch(`/tools/workspaces/${_enc(workspaceId)}/unpack`, { method: 'POST' });
}

/** GET workspaces visible in scope. options: { networkId, poolId }. → { secrets, count } */
export async function listWorkspaces({ networkId = '', poolId = '' } = {}) {
  const p = new URLSearchParams();
  if (networkId) p.set('networkId', networkId);
  if (poolId) p.set('poolId', poolId);
  const qs = p.toString();
  return authedFetch(`/tools/workspaces${qs ? `?${qs}` : ''}`);
}

/** DELETE a workspace (row + S3 objects). 409 if any agent still pins it. */
export async function deleteWorkspace(workspaceId) {
  return authedFetch(`/tools/workspaces/${_enc(workspaceId)}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------------
// Tenant-authored SKILLS (doc/arch/vertical/SKILLS.md) — reusable claude-discoverable
// assets. Distinct from the curated ``workspace-skills`` allowlist above: these
// are authored BY a tenant, FOR that tenant's own workspaces. v1 TENANT scope.
// ---------------------------------------------------------------------------

/** POST create a tenant skill from the three intake fields.
 *  body: { name, description, instructions, selfImproving?, scope? }
 *  → { skillId, name, description, selfImproving, version, status:'READY' }.
 *  Create writes a deterministic SKILL.md scaffold (no blind LLM call) and is
 *  READY immediately; refine it later via updateSkill or in a room. */
export async function createSkill(body) {
  return authedFetch('/tools/skills', { method: 'POST', body: JSON.stringify(body || {}) });
}

/** GET tenant skills visible in scope. options: { networkId, poolId }.
 *  → { secrets, count } (same slim-row shape the registry list returns). */
export async function listSkills({ networkId = '', poolId = '' } = {}) {
  const p = new URLSearchParams();
  if (networkId) p.set('networkId', networkId);
  if (poolId) p.set('poolId', poolId);
  const qs = p.toString();
  return authedFetch(`/tools/skills${qs ? `?${qs}` : ''}`);
}

/** GET one skill's full detail (row + SKILL.md body + MEMORY.md if self-improving).
 *  → { skillId, name, description, selfImproving, version, status, body, memory } */
export async function getSkillDetail(skillId) {
  return authedFetch(`/tools/skills/${_enc(skillId)}`);
}

/** PUT re-author a skill. Two modes:
 *  - structured: { name?, description?, instructions?, selfImproving? } → re-render
 *  - raw:        { rawBody: '<full SKILL.md>' } → write verbatim (advanced)
 *  Version bumps on every save. → { skillId, version, status }. */
export async function updateSkill(skillId, body) {
  return authedFetch(`/tools/skills/${_enc(skillId)}`, { method: 'PUT', body: JSON.stringify(body || {}) });
}

/** DELETE a skill (row + S3 objects). 409 if any agent still pins it. */
export async function deleteSkill(skillId) {
  return authedFetch(`/tools/skills/${_enc(skillId)}`, { method: 'DELETE' });
}

/** POST "✨ Improve with AI" — sharpen a skill's draft. Stateless: persists
 *  nothing, returns a suggestion the UI shows as accept/reject.
 *  body: { name?, description?, instructions? }
 *  → { improved, original:{description,instructions}, suggestion:{description,instructions,why} } */
export async function refineSkill(draft) {
  return authedFetch('/tools/skills/refine', { method: 'POST', body: JSON.stringify(draft || {}) });
}

/** POST preview the KB consolidation across every deployed copy of a skill.
 *  → { consensus: [...], contradictions: [{trigger, options:[{fix,sources,count}]}],
 *      sourceCount, scannedWorkspaces }. Mutates nothing — the admin reviews,
 *  resolves contradictions, then calls releaseConsolidatedSkill. */
export async function consolidateSkill(skillId) {
  return authedFetch(`/tools/skills/${_enc(skillId)}/consolidate`, { method: 'POST' });
}

/** POST persist the admin-approved consolidated KB → master skill, version++.
 *  body: { lessons: [{trigger, fix, signal?, count?}] } (consensus + the chosen
 *  fix per contradiction). → { skillId, version, lessonCount, evicted }. */
export async function releaseConsolidatedSkill(skillId, lessons) {
  return authedFetch(`/tools/skills/${_enc(skillId)}/consolidate/release`, {
    method: 'POST', body: JSON.stringify({ lessons: lessons || [] }),
  });
}

/** GET a shared workspace's tree (lazy, one folder). Reuses the workspace
 *  browser via ``?workspaceId=``. → same shape as getAgentWorkspaceTree. */
export async function getSharedWorkspaceTree(networkId, agentId, workspaceId, prefix = '') {
  const p = new URLSearchParams({ workspaceId: String(workspaceId || '') });
  if (prefix) p.set('prefix', String(prefix));
  return authedFetch(`/tools/workspace/${_enc(networkId)}/${_enc(agentId)}/tree?${p.toString()}`);
}

// --- Tenant-workspace READ-ONLY VIEW (by workspaceId, no network/agent) ------
// Backend authes on the workspace row's tenant when ?workspaceId= is present, so
// the network/agent path segments are placeholders ("_"). Powers the Connections
// tab "View" — inspect a workspace's files + baked template/skill versions.

/** GET one folder of a workspace by id (lazy). prefix='' = root. */
export async function viewWorkspaceTree(workspaceId, prefix = '') {
  const p = new URLSearchParams({ workspaceId: String(workspaceId || '') });
  if (prefix) p.set('prefix', String(prefix));
  return authedFetch(`/tools/workspace/_/_/tree?${p.toString()}`);
}

/** GET one file's inline text from a workspace by id. */
export async function viewWorkspaceFile(workspaceId, path) {
  const p = new URLSearchParams({ workspaceId: String(workspaceId || ''), path: String(path || '') });
  return authedFetch(`/tools/workspace/_/_/file?${p.toString()}`);
}

/** GET a presigned URL for one workspace object (any type; viewer/download). */
export async function viewWorkspaceRawUrl(workspaceId, path, { download = false } = {}) {
  const p = new URLSearchParams({ workspaceId: String(workspaceId || ''), path: String(path || '') });
  if (download) p.set('download', '1');
  return authedFetch(`/tools/workspace/_/_/raw?${p.toString()}`);
}

// --- WORKSPACE PIN overrides (room-drawer "Tools") -------------------------
// Room-wide is the primary model (one shared workspace for the whole room —
// the runtime lease lets every agent collaborate on the one tree). The
// per-agent variants below remain for backward-compat with legacy rows; the
// resolver still prefers a per-agent pin where one exists.

/** PUT pin ONE workspace for the whole room (pool-wide). */
export async function setPoolWorkspaceOverride(networkId, poolId, workspaceId) {
  return authedFetch(
    `/tools/workspace-overrides/${_enc(networkId)}/${_enc(poolId)}/${_enc(workspaceId)}`,
    { method: 'PUT', body: '{}' },
  );
}

/** DELETE clear the room-wide workspace pin. */
export async function clearPoolWorkspaceOverride(networkId, poolId, workspaceId) {
  return authedFetch(
    `/tools/workspace-overrides/${_enc(networkId)}/${_enc(poolId)}/${_enc(workspaceId)}`,
    { method: 'DELETE' },
  );
}

/** PUT pin a workspace to an agent (legacy per-agent pin). */
export async function setAgentWorkspaceOverride(networkId, poolId, agentId, workspaceId) {
  return authedFetch(
    `/tools/workspace-overrides/${_enc(networkId)}/${_enc(poolId)}/agents/${_enc(agentId)}/${_enc(workspaceId)}`,
    { method: 'PUT', body: '{}' },
  );
}

/** DELETE clear an agent's workspace pin. */
export async function clearAgentWorkspaceOverride(networkId, poolId, agentId, workspaceId) {
  return authedFetch(
    `/tools/workspace-overrides/${_enc(networkId)}/${_enc(poolId)}/agents/${_enc(agentId)}/${_enc(workspaceId)}`,
    { method: 'DELETE' },
  );
}

/** GET the room's workspace pins. → { overrides:[{agentId?, workspaceId, ...}] } */
export async function listAgentWorkspaceOverrides(networkId, poolId) {
  return authedFetch(`/tools/workspace-overrides/${_enc(networkId)}/${_enc(poolId)}`);
}

export const __TEST_ONLY__ = {
  WORKBENCH_POOL_SLUG,
};
